import { Component, computed, effect, inject, signal, OnDestroy } from '@angular/core';
import { MatCardModule } from "@angular/material/card";
import { MatChipsModule } from '@angular/material/chips';
import { AllChecks } from "../../checks/all-checks/all-checks";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatButtonModule } from "@angular/material/button";
import { Project } from '../../../core/models/project-model';
import { Avatar } from '../../../core/models/avatar-model';
import { MatDialog } from '@angular/material/dialog';
import { NewCheckDialog } from '../dialogs/new-check-dialog/new-check-dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from "@angular/material/input";
import { Layout } from '../../../core/common/ui/layout';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ProjectService } from '../../../core/projects/project';

@Component({
  selector: 'app-project-details',
  imports: [MatCardModule, MatChipsModule, AllChecks, MatExpansionModule, MatSidenavModule, MatButtonModule, MatIconModule, MatInputModule],
  templateUrl: './project-details.html',
  styleUrl: './project-details.scss',
})
export class ProjectDetails implements OnDestroy {
    readonly layout = inject(Layout);
    readonly route = inject(ActivatedRoute);
    readonly projectService = inject(ProjectService);
    readonly dialog = inject(MatDialog);

    isDrawerOpen = false;

    // projectCode aus Route-Params
    readonly projectCode = toSignal(
      this.route.params.pipe(map(p => p['projectCode'] as string ?? null)),
      { initialValue: null }
    );

    // Projekt direkt von API laden
    readonly project = toSignal(
      this.route.params.pipe(
        map(p => p['projectCode'] as string ?? null),
        switchMap(code => code ? this.projectService.getProjectByCodeApi(code) : of(null)),
      ),
      { initialValue: null }
    );

    // Platzhalter: leere usersById Map bis Check-API-Migration (User-Referenzen in Checks)
    readonly usersById = computed<Map<string, Avatar>>(() => new Map());

    // projectsById als Map<string, Project> für AllChecks
    readonly projectsById = computed<Map<string, Project>>(() => {
      const p = this.project();
      if (!p) return new Map();
      return new Map([[p.contractNr, p]]);
    });

    constructor() {
      effect(() => {
        this.isDrawerOpen = this.layout.isProjectNavOpen();
      });
    }

    readonly projectDataViewModel = computed(() => {
      const p = this.project();
      if (!p) return null;
      return {
        contractNr: p.contractNr,
        displayName: p.displayName,
        phase: p.phase,
        departmentName: p.department.displayName,
      };
    });

    readonly newCheckId = signal<string | null>(null);

    openDialog() {
      const code = this.projectCode();
      if (!code) return;
      const dialogRef = this.dialog.open(NewCheckDialog, {
        data: { projectCode: code }
      });
      dialogRef.afterClosed().subscribe((result: string | void) => {
        if (typeof result === 'string') {
          this.newCheckId.set(result);
        }
      });
    }

    ngOnDestroy(): void {
      this.newCheckId.set(null);
    }
}
